import { useState, useEffect, useRef } from "react"
import { Link, useNavigate, useLocation } from "react-router-dom"
import { isLoggedIn, clearToken } from "../auth"
import { Menu, X, Shield, BarChart3, HelpCircle, ChevronDown, Mic, Globe, Phone } from "lucide-react"
import { useLanguage } from "../context/LanguageContext"

const Header = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { language, toggleLanguage, t } = useLanguage()
  const [isAdmin, setIsAdmin] = useState(false)
  const [loading, setLoading] = useState(true)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isAnalyzerDropdownOpen, setIsAnalyzerDropdownOpen] = useState(false)
  const [isMobileAnalyzersOpen, setIsMobileAnalyzersOpen] = useState(false)
  const dropdownRef = useRef(null)

  const analyzers = [
    { id: "home-improvement", label: "Home Improvement", labelEs: "Mejoras del Hogar", available: true },
  ]

  const isAnalyzerActive = location.pathname.startsWith("/analyzer")
  const isAuthPage = ["/login", "/register", "/forgot-password", "/reset-password"].includes(location.pathname)
  const isLandingPage = location.pathname === "/"
  
  // Don't show header on landing page when not logged in, or on auth pages
  const shouldHideHeader = (!isLoggedIn() && isLandingPage) || isAuthPage

  const handleLogout = () => {
    clearToken()
    closeAllMenus()
    navigate("/")
  }

  const toggleMobileMenu = () => setIsMobileMenuOpen((v) => !v)
  const closeAllMenus = () => {
    setIsMobileMenuOpen(false)
    setIsAnalyzerDropdownOpen(false)
    setIsMobileAnalyzersOpen(false)
  }

  useEffect(() => {
    const onClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsAnalyzerDropdownOpen(false)
      }
    }
    document.addEventListener("mousedown", onClickOutside)
    return () => document.removeEventListener("mousedown", onClickOutside)
  }, [])

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!isLoggedIn()) {
        setLoading(false)
        return
      }
      try {
        const token = localStorage.getItem("token")
        const response = await fetch("https://call-analyzer-backend.onrender.com/admin-check", {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store'
        })
        setIsAdmin(response.ok)
      } catch (err) {
        console.error("Admin check failed:", err)
        setIsAdmin(false)
      } finally {
        setLoading(false)
      }
    }
    checkAdminStatus()
  }, [location.pathname])

  // Don't render header in certain cases
  if (shouldHideHeader) {
    return null
  }

  return (
    <header className="bg-white/95 backdrop-blur-sm border-b border-[#CED7DB] sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 flex-shrink-0" onClick={closeAllMenus}>
            <img src="/New_Logo.png" alt="NeuroTrain BOT" className="h-12 w-auto" />
            <span className="text-xl font-bold text-[#0A4B4B] hidden sm:block">NeuroTrain BOT</span>
            <span className="text-lg font-bold text-[#0A4B4B] sm:hidden">NT</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            {isLoggedIn() ? (
              <>
                {/* Analyzers Dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsAnalyzerDropdownOpen((v) => !v)}
                    className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isAnalyzerActive ? "text-emerald-700 bg-emerald-50" : "text-gray-700 hover:text-[#0A4B4B]"
                    }`}
                  >
                    <Shield className="h-4 w-4" />
                    <span>Analyzers</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${isAnalyzerDropdownOpen ? "rotate-180" : ""}`} />
                  </button>

                  {isAnalyzerDropdownOpen && (
                    <div className="absolute top-full mt-2 left-0 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                      
                      {analyzers.map((an) => (
                        <Link
                          key={an.id}
                          to={an.available ? `/analyzer/${an.id}` : "#"}
                          onClick={() => an.available && closeAllMenus()}
                          className={`block px-4 py-2 text-sm transition ${
                            an.available
                              ? "text-gray-700 hover:bg-emerald-50 hover:text-emerald-700"
                              : "text-gray-400 cursor-not-allowed"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span>{an.label}</span>
                            {!an.available && (
                              <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">Soon</span>
                            )}
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Transcribe Link */}
                <Link
                  to="/transcribe"
                  className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    location.pathname === "/transcribe" ? "text-emerald-700 bg-emerald-50" : "text-gray-700 hover:text-[#0A4B4B]"
                  }`}
                >
                  <Mic className="h-4 w-4" />
                  <span>{language === 'es' ? 'Transcribir' : 'Transcribe'}</span>
                </Link>

                {/* CallTools Link */}
                <Link
                  to="/calltools"
                  className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    location.pathname === "/calltools" ? "text-emerald-700 bg-emerald-50" : "text-gray-700 hover:text-[#0A4B4B]"
                  }`}
                >
                  <Phone className="h-4 w-4" />
                  <span>CallTools</span>
                </Link>

                {/* Dashboard Link - Only for non-admins */}
                {!isAdmin && (
                  <Link
                    to="/dashboard"
                    className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      location.pathname === "/dashboard" ? "text-emerald-700 bg-emerald-50" : "text-gray-700 hover:text-[#0A4B4B]"
                    }`}
                  >
                    <BarChart3 className="h-4 w-4" />
                    <span>Dashboard</span>
                  </Link>
                )}

                <Link
                  to="/help"
                  className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    location.pathname === "/help" ? "text-emerald-700 bg-emerald-50" : "text-gray-700 hover:text-[#0A4B4B]"
                  }`}
                >
                  <HelpCircle className="h-4 w-4" />
                  <span>{t('help')}</span>
                </Link>

                {/* Language Toggle */}
                <button
                  onClick={toggleLanguage}
                  className={language === 'en' 
                    ? "flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-bold bg-orange-500 text-white hover:bg-orange-600 transition-colors"
                    : "flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-bold bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                  }
                  title={language === 'en' ? 'Cambiar a Español' : 'Switch to English'}
                >
                  <Globe className="h-4 w-4" />
                  <span>{language === 'en' ? 'ESPAÑOL' : 'ENGLISH'}</span>
                </button>

                {/* Admin Links */}
                {isAdmin && (
                  <>
                    <Link
                      to="/analytics"
                      className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        location.pathname === "/analytics"
                          ? "text-emerald-700 bg-emerald-50"
                          : "text-gray-700 hover:text-[#0A4B4B]"
                      }`}
                    >
                      <BarChart3 className="h-4 w-4" />
                      <span>Analytics</span>
                    </Link>
                    <Link
                      to="/admin"
                      className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        location.pathname === "/admin"
                          ? "text-emerald-700 bg-emerald-50"
                          : "text-gray-700 hover:text-[#0A4B4B]"
                      }`}
                    >
                      <Shield className="h-4 w-4" />
                      <span>Admin</span>
                    </Link>
                  </>
                )}

                <button
                  onClick={handleLogout}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-gray-700 hover:text-[#0A4B4B] px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="bg-[#0A4B4B] hover:bg-[#083d3d] text-white px-4 py-2 rounded-md text-sm font-medium transition-all duration-200"
                >
                  Get Started
                </Link>
              </>
            )}
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="text-gray-700 hover:text-[#0A4B4B] p-2 rounded-md transition-colors"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {isLoggedIn() ? (
                <>
                  <button
                    onClick={() => setIsMobileAnalyzersOpen((v) => !v)}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-[#0A4B4B] hover:bg-gray-50 transition-colors"
                  >
                    <span>📊 Analyzers</span>
                    <ChevronDown className={`h-5 w-5 transition-transform ${isMobileAnalyzersOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isMobileAnalyzersOpen && (
                    <div className="ml-2 space-y-1">
                      <Link
                        to="/analyzer/home-improvement"
                        onClick={closeAllMenus}
                        className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-emerald-700 hover:bg-emerald-50 transition-colors"
                      >
                        All Analyzers
                      </Link>
                      {analyzers.map((an) => (
                        <Link
                          key={an.id}
                          to={an.available ? `/analyzer/${an.id}` : "#"}
                          onClick={() => an.available && closeAllMenus()}
                          className={`block px-3 py-2 rounded-md text-base font-medium transition-colors ${
                            an.available
                              ? "text-gray-700 hover:text-emerald-700 hover:bg-emerald-50"
                              : "text-gray-400 cursor-not-allowed"
                          }`}
                        >
                          {an.label}
                          {!an.available && <span className="ml-2 text-xs text-gray-500">(Soon)</span>}
                        </Link>
                      ))}
                    </div>
                  )}

                  <Link
                    to="/transcribe"
                    onClick={closeAllMenus}
                    className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-[#0A4B4B] hover:bg-gray-50 transition-colors"
                  >
                    🎤 {language === 'es' ? 'Transcribir' : 'Transcribe'}
                  </Link>

                  <Link
                    to="/calltools"
                    onClick={closeAllMenus}
                    className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-[#0A4B4B] hover:bg-gray-50 transition-colors"
                  >
                    📞 CallTools
                  </Link>

                  {!loading && !isAdmin && (
                     <Link
                        to="/dashboard"
                        onClick={closeAllMenus}
                        className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-[#0A4B4B] hover:bg-gray-50 transition-colors"
                      >
                        📈 Dashboard
                      </Link>
                  )}
                  
                  <Link
                    to="/help"
                    onClick={closeAllMenus}
                    className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-[#0A4B4B] hover:bg-gray-50 transition-colors"
                  >
                    ❓ Help
                  </Link>

                  {isAdmin && (
                    <>
                      <Link
                        to="/analytics"
                        onClick={closeAllMenus}
                        className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-[#0A4B4B] hover:bg-gray-50 transition-colors"
                      >
                        📈 Analytics
                      </Link>
                      <Link
                        to="/admin"
                        onClick={closeAllMenus}
                        className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-[#0A4B4B] hover:bg-gray-50 transition-colors"
                      >
                        ⚙️ Admin
                      </Link>
                    </>
                  )}
                  <div className="border-t border-gray-200 pt-2 mt-2">
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-red-600 hover:bg-red-50 transition-colors"
                    >
                      🚪 Logout
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={closeAllMenus}
                    className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-[#0A4B4B] hover:bg-gray-50 transition-colors"
                  >
                    🔑 Sign In
                  </Link>
                  <Link
                    to="/register"
                    onClick={closeAllMenus}
                    className="block px-3 py-2 rounded-md text-base font-medium bg-[#0A4B4B] text-white hover:bg-[#083d3d] transition-colors mx-3 text-center"
                  >
                    🚀 Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  )
}

export default Header
